/**
 * Firebase client config: Firestore + Auth
 * Robust initialization with error handling
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';

// Validate environment variables
if (!process.env.REACT_APP_FIREBASE_API_KEY) {
  console.error('❌ REACT_APP_FIREBASE_API_KEY is missing! Check your .env file.');
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyDrlmO-U-Ek-Pd14D2PlSX5dnD-NO2f8GU',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'apkakhata-84547.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'apkakhata-84547',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'apkakhata-84547.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '915888791310',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:915888791310:web:07e4480413e5eea848015f',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-TC611SYQVG',
};

// Ensure singleton Firebase app instance
let app;
try {
  app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
} catch (error) {
  console.error('Firebase initialization error:', error);
  // Fallback: try to get existing app
  app = getApp();
}

// Initialize services with error handling
let auth, db, database;
try {
  auth = getAuth(app);
  db = getFirestore(app);
  database = getDatabase(app);
} catch (error) {
  console.error('Firebase service initialization error:', error);
  // Re-initialize if there's an issue
  if (!auth) auth = getAuth(app);
  if (!db) db = getFirestore(app);
  if (!database) database = getDatabase(app);
}

// Google Auth Provider (singleton)
let googleProvider;
try {
  googleProvider = new GoogleAuthProvider();
  googleProvider.addScope('profile');
  googleProvider.addScope('email');
} catch (error) {
  console.error('Google Provider initialization error:', error);
  googleProvider = new GoogleAuthProvider();
}

// Global error handler for Firestore internal errors
if (typeof window !== 'undefined') {
  // Handle uncaught errors
  const originalErrorHandler = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Check if this is a Firestore internal assertion error
    const errorMessage = message?.toString() || '';
    const isFirestoreInternalError = 
      errorMessage.includes('FIRESTORE') && 
      errorMessage.includes('INTERNAL ASSERTION');
    
    if (isFirestoreInternalError) {
      // Suppress Firestore internal errors - they're harmless
      console.warn('⚠️ Firestore internal assertion error (suppressed):', message);
      return true; // Prevent default error handling
    }
    
    // Call original error handler for other errors
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', function(event) {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';
    const isFirestoreInternalError = 
      errorMessage.includes('FIRESTORE') && 
      errorMessage.includes('INTERNAL ASSERTION');
    
    if (isFirestoreInternalError) {
      // Suppress Firestore internal errors
      console.warn('⚠️ Firestore internal assertion error in promise (suppressed):', event.reason);
      event.preventDefault(); // Prevent default error handling
    }
  });

  // Override console.error to suppress Firestore internal errors
  const originalConsoleError = console.error;
  console.error = function(...args) {
    const errorString = args.join(' ');
    if (errorString.includes('FIRESTORE') && errorString.includes('INTERNAL ASSERTION')) {
      // Log as warning instead of error
      console.warn('⚠️ Firestore internal assertion (suppressed):', ...args);
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

export { auth, db, database, googleProvider };
export const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'amit.mca.pu@gmail.com';

export default app;
